import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DEPLOYMENT_KIND_LABELS,
  DEPLOYMENT_RETURN_REASON_LABELS,
  FirmwareTrack,
  kstDateTime,
  REMEDIATION_STATUS_LABELS,
  ROBOT_LOCATION_LABELS,
  ROBOT_STATUS_LABELS,
  ROBOT_VERIFICATION_LABELS,
  SERVICE_ORDER_STATUS_LABELS,
  SERVICE_SEVERITY_LABELS,
  UserRole,
  canOperate,
  type ContractSummaryDto,
  type FaultRemediationDto,
  type DeploymentSummaryDto,
  type OwnerEntity,
  type RobotSummaryDto,
} from '@bhops/shared';
import { deploymentsApi } from '@/api/deployments';
import { DeploymentStatusCell } from '@/lib/deployment-status-label';
import { contractsApi } from '@/api/contracts';
import { customerSheetReportsApi } from '@/api/customerSheetReports';
import { ReportLinkPicker } from '@/pages/field/ReportLinkPicker';
import {
  pill,
  REMEDIATION_TONE,
  SEVERITY_TONE,
  SHEET_STATUS_TONE,
  SHEET_STATUS_TONE_UNKNOWN,
} from '@/pages/field/field-tones';
import { faultRemediationsApi } from '@/api/faultRemediations';
import { robotsApi } from '@/api/robots';
import {
  firmwareEventsApi,
  type FirmwareEventDto,
  type LatestFirmwareEventEntry,
} from '@/api/firmware-events';
import { Button } from '@/components/Form';
import { SpareBadge } from '@/components/SpareBadge';
import { useAuthStore } from '@/stores/auth';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { FirmwareEventCreateModal } from './FirmwareEventCreateModal';
import { isOtherEntityRobot, OTHER_ENTITY_HISTORY_NOTE } from './robot-entity';
import {
  Fact,
  FactList,
  FactSection,
  Sheet,
  SheetEmpty,
  SheetHead,
  SheetTable,
} from './robot-detail-ui';

// Plan §D-4 (PDF p.9 / p.12 / p.21) — 6 domain tabs for the robot
// detail page: 출고 이력 / 계약 / 제조 이력 / 필드 서비스 / 펌웨어 / 역추적.
// All five composed from existing data sources (Deployment / Contract /
// Robot.manufacture* / Report) + the new RobotFirmwareEvent model.

const TRACK_LABEL: Record<FirmwareTrack, string> = {
  BINARY: '바이너리',
  BUNDLE: '번들',
  TABLET_APP: '태블릿 앱',
};

// =============================================================
// 다른 법인 로봇 안내 — 출고 이력·계약 탭 머리
// =============================================================
//
// 오너 결정 2026-10-20 — 직원도 허니컴로보틱스 로봇을 보고 고친다. 넓어진 건 로봇뿐이라 배치·
// 계약 목록은 여전히 자기 법인 것만 내려온다. 허니컴로보틱스 이름으로 남은 출고·계약이 조용히
// 빠지면 빈 목록이 "출고된 적 없음"으로 읽히므로, 다른 법인 로봇에서는 목록(빈 안내 포함) 위에
// 빠진 것이 있다고 적어 둔다. 관리자는 법인을 넘어 다 보므로 붙이지 않는다.

/** 이 탭들이 받는 로봇 — 법인만 본다. */
type TabRobot = { ownerEntity: OwnerEntity };

function OtherEntityHistoryNote({ robot }: { robot: TabRobot }) {
  const user = useAuthStore((s) => s.user);
  if (!isOtherEntityRobot(user, robot)) return null;
  return (
    <div data-testid="robot-other-entity-history-note" style={{ padding: '0 24px 12px' }}>
      <Hint text={OTHER_ENTITY_HISTORY_NOTE} />
    </div>
  );
}

// =============================================================
// 출고 이력 탭 — Deployment time series for this robot
// =============================================================

export function DeploymentsTab({ robotId, robot }: { robotId: string; robot: TabRobot }) {
  const q = useQuery({
    queryKey: ['robot-deployments', robotId],
    queryFn: () => deploymentsApi.list({ robotId, pageSize: 100 }),
    enabled: !!robotId,
  });
  const rows = q.data?.items ?? [];

  // R2 후속 #2 — 매장 ID/종류/사유 대신 고객사(상호)/매장(위치)/출고일/상태.
  // deployments.service.list 가 customer/store 를 include 하므로 별도 조회 불필요.
  // round5 #19 — 구분(kind) 배지 + 이벤트명 표기 복원: 교육/전시 임시 이동이
  // 고객 출고와 구분 없이 보이던 문제(list API 가 이미 내려주던 필드).
  // 2026-09-23 — 정본을 쓴다. 종전엔 여기 네 개만 적혀 있어, 인증·시험(CERTIFICATION)·
  // 테스트(TEST) 배치가 영문 코드로 떴다. 표가 `Record<string>` 이라 타입 검사도 못 잡았다.
  const KIND_LABEL: Record<string, string> = DEPLOYMENT_KIND_LABELS;
  return (
    <Sheet>
      <SheetHead title="출고 이력" count={q.isLoading || q.isError ? undefined : rows.length} />
      {/* 비욘드허니컴 배치가 일부 있어 표가 그려져도 허니컴로보틱스 쪽 행은 빠져 있다. */}
      <OtherEntityHistoryNote robot={robot} />
      {q.isLoading ? (
        <SheetEmpty>불러오는 중…</SheetEmpty>
      ) : q.isError ? (
        <SheetEmpty>
          <ErrorBox text="출고 이력을 불러오지 못했습니다." />
        </SheetEmpty>
      ) : rows.length === 0 ? (
        <SheetEmpty>아직 출고된 적이 없습니다.</SheetEmpty>
      ) : (
        <SheetTable>
          <table style={tableBase}>
            <thead>
              <tr>
                <th style={th}>구분</th>
                <th style={th}>고객사</th>
                <th style={th}>매장</th>
                <th style={th}>출고일</th>
                {/* 개선계획 §7 — 회수일/사유/메모가 상태 셀의 '회수 YYYY-MM-DD' 한 줄에
                  묻혀 있어 "왜 돌아왔는지"를 화면에서 알 수 없었다. 전용 칸으로 분리. */}
                <th style={th}>회수일</th>
                <th style={th}>사유</th>
                <th style={th}>상태</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d: DeploymentSummaryDto) => {
                const storeName = d.store
                  ? [d.store.name, d.store.branchName].filter(Boolean).join(' ')
                  : null;
                return (
                  <tr key={d.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={td}>
                      <span
                        data-testid="deployment-kind-badge"
                        style={{
                          display: 'inline-block',
                          padding: '1px 8px',
                          borderRadius: 'var(--r-pill)',
                          fontSize: 12,
                          fontWeight: 600,
                          background:
                            d.kind === 'CUSTOMER'
                              ? 'var(--bg-info-subtle)'
                              : 'var(--bg-accent-subtle)',
                          color:
                            d.kind === 'CUSTOMER' ? 'var(--fg-on-info-tint)' : 'var(--fg-brand)',
                        }}
                      >
                        {KIND_LABEL[d.kind] ?? d.kind}
                      </span>
                      {d.isSpare && <SpareBadge testId="deployment-spare-badge" />}
                      {d.eventName && <div style={subLine}>{d.eventName}</div>}
                    </td>
                    <td style={tdStrong}>{d.customer?.displayName ?? <Dash />}</td>
                    <td style={td}>
                      {storeName ?? (
                        <span style={{ color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)' }}>
                          {d.storeId.slice(0, 8)}…
                        </span>
                      )}
                      {d.store?.address && <div style={subLine}>{d.store.address}</div>}
                    </td>
                    <td style={tdMono}>{kstYmd(d.deployedAt) ?? <Dash />}</td>
                    <td style={tdMono}>{kstYmd(d.returnedAt) ?? <Dash />}</td>
                    <td style={td}>
                      {d.returnReason ? DEPLOYMENT_RETURN_REASON_LABELS[d.returnReason] : <Dash />}
                      {/* Deployment.notes 는 배치/회수가 공유하는 한 칸이라, 회수된
                        행에서만 회수 메모로 읽는다. */}
                      {d.returnedAt && d.notes && <div style={subLine}>{d.notes}</div>}
                    </td>
                    <td style={td}>
                      <DeploymentStatusCell kind={d.kind} returnedAt={d.returnedAt} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SheetTable>
      )}
    </Sheet>
  );
}

// =============================================================
// 계약 탭 — Contracts that include this robot via Deployment
// =============================================================

export function ContractTab({ robotId, robot }: { robotId: string; robot: TabRobot }) {
  // Reuse the deployments list and pull unique customer/store
  // pairs; for each, look up active contracts. Server-side this
  // would be one join query; until that lands we do it client-side.
  const depsQ = useQuery({
    queryKey: ['robot-deployments', robotId],
    queryFn: () => deploymentsApi.list({ robotId, pageSize: 100 }),
  });
  const customerIds = useMemo(() => {
    const set = new Set<string>();
    for (const d of depsQ.data?.items ?? []) {
      if (d.customerId) set.add(d.customerId);
    }
    return Array.from(set);
  }, [depsQ.data]);

  const contractsQ = useQuery({
    queryKey: ['robot-contracts', robotId, customerIds.sort().join(',')],
    queryFn: async () => {
      // One list call per customer; small N — usually 1-2.
      const results = await Promise.all(
        customerIds.map((cid) => contractsApi.list({ customerId: cid })),
      );
      return results.flatMap((r) => r.items ?? []);
    },
    enabled: customerIds.length > 0,
  });

  if (depsQ.isLoading || contractsQ.isLoading) return <Empty text="불러오는 중…" />;
  if (customerIds.length === 0)
    return (
      <>
        <OtherEntityHistoryNote robot={robot} />
        <Empty text="이 로봇이 출고된 적이 없어 연관 계약을 찾을 수 없습니다." />
      </>
    );
  const rows = contractsQ.data ?? [];
  if (rows.length === 0)
    return (
      <>
        <OtherEntityHistoryNote robot={robot} />
        <Empty text="연관된 계약이 없습니다." />
      </>
    );

  return (
    <>
      <OtherEntityHistoryNote robot={robot} />
      <table style={tableBase}>
        <thead>
          <tr>
            <th style={th}>계약</th>
            <th style={th}>고객사</th>
            <th style={th}>상태</th>
            <th style={th}>기간</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c: ContractSummaryDto) => (
            <tr key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={tdMono}>{c.humanId}</td>
              <td style={td}>{c.customer?.displayName ?? '-'}</td>
              <td style={td}>{c.status}</td>
              <td style={tdMono}>
                {kstYmd(c.startDate) ?? '-'} ~ {kstYmd(c.endDate) ?? '-'}
              </td>
              <td style={td}>
                {/* FB §3 — 계약 상세 미사용 → 고객사 상세로. (계약 DTO 의
                  customerId 항상 포함.) */}
                <Link to={`/customers/${c.customerId}`} style={{ color: 'var(--blue-800)' }}>
                  상세 →
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// =============================================================
// 제조 이력 탭 — Robot's manufacture metadata + Phase 3 fields
// =============================================================

export function ManufactureTab({ robot }: { robot: RobotSummaryDto }) {
  // FB2 §5C (결정 D) — "로더 종류"(loaderType) 표시 제거(구분 사양과 중복).
  // DB 컬럼은 보존하나 상세 화면에선 더 이상 노출하지 않는다.
  const r = robot as RobotSummaryDto & {
    color?: string | null;
    manufactureCompletedAt?: string | null;
    binaryUpdatedAt?: string | null;
  };
  // 'YYMM'(2511) → '2025.11'. 개요의 제조 · 출고와 같은 모양.
  const ym = (v: string | null | undefined) =>
    v && /^\d{4}$/.test(v) ? `20${v.slice(0, 2)}.${v.slice(2)}` : v;
  return (
    <Sheet>
      <SheetHead title="제조 이력" />
      <div className="rd-cols">
        <FactSection title="모델 · 사양">
          <FactList>
            <Fact
              k="모델"
              v={
                <>
                  <span className="rd-mono">{robot.model.code}</span>
                  {robot.model.displayName && robot.model.displayName !== robot.model.code && (
                    <span style={{ color: 'var(--fg-muted)', fontWeight: 400 }}>
                      {' '}
                      {robot.model.displayName}
                    </span>
                  )}
                </>
              }
            />
            <Fact k="색상" v={r.color} />
            {/* 2026-11-02 — 영문 코드(CONFIRMED)가 그대로 보이던 것을 정본 라벨로. */}
            <Fact
              k="검증 상태"
              v={ROBOT_VERIFICATION_LABELS[robot.verificationStatus] ?? robot.verificationStatus}
            />
          </FactList>
        </FactSection>
        <FactSection title="날짜">
          <FactList>
            <Fact k="첫 제조 월" v={ym(robot.firstProductionYm)} mono />
            <Fact k="제조 완료일" v={kstYmd(r.manufactureCompletedAt)} mono />
            <Fact k="첫 출고 월" v={ym(robot.firstShipmentYm)} mono />
            <Fact k="바이너리 업데이트" v={kstYmd(r.binaryUpdatedAt)} mono />
          </FactList>
        </FactSection>
      </div>
    </Sheet>
  );
}

// =============================================================
// 고장 이력 탭 — 이 로봇의 조치 이력 + 그 조치가 가리키는 고객사 리포트
// =============================================================
//
// 2026-09-14 개편(오너 결정). 종전에는 `ServiceOrder` 와 QR `CustomerReport` 를
// 보여 줬는데, 현장 기록의 정본이 **필드팀 유지보수 시트**로 옮겨 가면서 이 탭도
// 그쪽을 본다:
//
//   1) 고장 조치 이력 (FaultRemediation) — 로봇 **시리얼**로 찾는다
//   2) 고객사 리포트 (CustomerSheetReport) — 위 조치가 **리포트 번호로 연결해 둔**
//      신고들. 리포트 자체에는 로봇 정보가 없어 이 경로 말고는 이을 방법이 없다.
//
// 조치가 위에 오는 이유: 이 탭의 질문은 "이 로봇에 무슨 일을 했나" 다. 신고는 그
// 일이 시작된 계기라 아래에 둔다.
//
// ⚠ 연결이 없는 조치는 신고 쪽에 안 나타난다. 자동 매칭이 못 붙였거나 아직 리포트
//   번호가 없는 경우이고, 그때는 조치 표의 '연결하기' 로 고치러 간다.

/** 표 안에서 한 줄로 읽히게 자른다. */
function trim60(v: string): string {
  return v.length > 60 ? v.slice(0, 60) + '\u2026' : v;
}

// 이 탭의 두 표는 Field 목록 화면과 **같은 문법**으로 보여야 한다 — 왼쪽 색 띠,
// 알약 배지, 브랜드색 링크. 배지 정의는 `pages/field/field-tones` 정본을 쓴다
// (여기서 다시 만들면 같은 상태가 화면마다 다른 색이 된다).
const fsTable: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 14 };
const fsTh: CSSProperties = {
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  whiteSpace: 'nowrap',
};
const fsTd: CSSProperties = {
  padding: '9px 12px',
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};

export function FieldServiceTab({ robotId }: { robotId: string }) {
  const remQ = useQuery({
    queryKey: ['robot-fault-remediations', robotId],
    queryFn: () => faultRemediationsApi.list({ robotId, pageSize: 50 }),
    enabled: !!robotId,
  });
  const reportsQ = useQuery({
    queryKey: ['robot-sheet-reports', robotId],
    queryFn: () => customerSheetReportsApi.list({ robotId, pageSize: 50 }),
    enabled: !!robotId,
  });

  const rems = remQ.data?.items ?? [];
  const reports = reportsQ.data?.items ?? [];
  const unlinked = rems.filter((r) => !r.matchedReport).length;
  // 연결 창을 띄운 조치. null 이면 닫혀 있다.
  const [linking, setLinking] = useState<FaultRemediationDto | null>(null);
  // 연결을 바꾸면 고객사 신고의 상태가 따라 바뀌고, 그 상태는 현장팀 시트에도
  // 되적힌다. 서버가 열람 전용을 막으므로 화면에서도 버튼을 감춘다 — 누르고
  // 나서 거부당하는 것이 가장 나쁜 모양이다.
  const canLink = useAuthStore((s) => (s.user ? canOperate(s.user.role) : false));

  return (
    <div className="rd-stack">
      {/* 조치 이력 — 이 로봇에 실제로 한 일 */}
      <Sheet>
        <SheetHead title="고장 조치 이력" count={rems.length} />
        {remQ.isLoading ? (
          <SheetEmpty>불러오는 중…</SheetEmpty>
        ) : remQ.isError ? (
          <SheetEmpty>
            <ErrorBox text="조치 이력을 불러오지 못했습니다." />
          </SheetEmpty>
        ) : rems.length === 0 ? (
          <SheetEmpty>이 로봇의 조치 이력이 없습니다.</SheetEmpty>
        ) : (
          <SheetTable>
            <table style={fsTable}>
              <thead>
                <tr>
                  <th style={{ ...fsTh, width: 4, padding: 0 }} aria-hidden />
                  <th style={fsTh}>상태</th>
                  <th style={fsTh}>연결 리포트</th>
                  <th style={fsTh}>오류 코드</th>
                  <th style={fsTh}>증상 요약</th>
                  <th style={fsTh}>조치 내용</th>
                  <th style={fsTh}>작성일시</th>
                </tr>
              </thead>
              <tbody>
                {rems.map((r) => {
                  const tone = REMEDIATION_TONE[r.status];
                  return (
                    <tr key={r.id}>
                      <td style={{ padding: 0, background: tone.bar }} aria-hidden />
                      <td style={fsTd}>
                        <span style={{ ...pill, background: tone.bg, color: tone.fg }}>
                          {REMEDIATION_STATUS_LABELS[r.status]}
                        </span>
                      </td>
                      <td style={{ ...fsTd, whiteSpace: 'nowrap' }}>
                        {r.matchedReport ? (
                          <Link
                            to={`/field/customer-reports?highlight=${encodeURIComponent(
                              r.matchedReport.sheetRowKey,
                            )}`}
                            style={{ color: 'var(--fg-brand)', fontWeight: 600 }}
                          >
                            {r.matchedReport.sheetRowKey}
                          </Link>
                        ) : canLink ? (
                          // 연결이 없으면 여기서 바로 잇는다. 종전에는 조치 이력
                          // 목록으로 보내기만 해서, 거기서 연필을 눌러 리포트
                          // 번호를 손으로 타이핑해야 했다(오너 신고 26-09-16).
                          <button
                            type="button"
                            onClick={() => setLinking(r)}
                            title="연결할 리포트를 골라 잇는다"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              color: 'var(--fg-brand)',
                              fontSize: 13,
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            연결하기
                          </button>
                        ) : (
                          <span style={{ color: 'var(--fg-muted)', fontSize: 13 }}>–</span>
                        )}
                      </td>
                      <td style={{ ...fsTd, whiteSpace: 'nowrap' }}>{r.errorCode || '–'}</td>
                      <td style={fsTd}>{trim60(r.symptom)}</td>
                      <td style={fsTd}>{r.action ? trim60(r.action) : '–'}</td>
                      <td style={{ ...fsTd, whiteSpace: 'nowrap' }}>
                        {kstDateTime(r.occurredAt) ?? '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SheetTable>
        )}
      </Sheet>

      {/* 고객사 리포트 — 위 조치가 리포트 번호로 이어 둔 신고들 */}
      <Sheet>
        <SheetHead title="고객사 리포트" count={reports.length} />
        {reportsQ.isLoading ? (
          <SheetEmpty>불러오는 중…</SheetEmpty>
        ) : reportsQ.isError ? (
          <SheetEmpty>
            <ErrorBox text="리포트를 불러오지 못했습니다." />
          </SheetEmpty>
        ) : reports.length === 0 ? (
          <SheetEmpty>
            {unlinked > 0
              ? `연결된 신고가 없습니다. 조치 ${unlinked}건이 아직 리포트 번호와 이어지지 않았습니다.`
              : '연결된 신고가 없습니다.'}
          </SheetEmpty>
        ) : (
          <SheetTable>
            <table style={fsTable}>
              <thead>
                <tr>
                  <th style={{ ...fsTh, width: 4, padding: 0 }} aria-hidden />
                  <th style={fsTh}>등급</th>
                  <th style={fsTh}>리포트 번호</th>
                  <th style={fsTh}>매장</th>
                  <th style={fsTh}>상태</th>
                  <th style={fsTh}>요약</th>
                  <th style={fsTh}>접수 시간</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => {
                  const tone = SEVERITY_TONE[r.severity];
                  return (
                    <tr key={r.id}>
                      <td style={{ padding: 0, background: tone.bar }} aria-hidden />
                      <td style={fsTd}>
                        <span style={{ ...pill, background: tone.bg, color: tone.fg }}>
                          {SERVICE_SEVERITY_LABELS[r.severity]}
                        </span>
                      </td>
                      <td style={{ ...fsTd, whiteSpace: 'nowrap' }}>
                        <Link
                          to={`/field/customer-reports?highlight=${encodeURIComponent(
                            r.sheetRowKey,
                          )}`}
                          style={{ color: 'var(--fg-brand)', fontWeight: 600 }}
                        >
                          {r.sheetRowKey}
                        </Link>
                      </td>
                      <td style={{ ...fsTd, whiteSpace: 'nowrap' }}>
                        {r.storeName?.trim() ? r.storeName : '–'}
                      </td>
                      <td style={fsTd}>
                        <span
                          style={{
                            ...pill,
                            ...(SHEET_STATUS_TONE[r.status] ?? SHEET_STATUS_TONE_UNKNOWN),
                          }}
                        >
                          {SERVICE_ORDER_STATUS_LABELS[r.status]}
                        </span>
                      </td>
                      <td style={fsTd}>{trim60(r.summary)}</td>
                      <td style={{ ...fsTd, whiteSpace: 'nowrap' }}>
                        {kstDateTime(r.receivedAt) ?? '–'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SheetTable>
        )}
      </Sheet>

      {linking && (
        <ReportLinkPicker
          // 창을 새로 그리게 해 이전 조치의 검색어·선택이 넘어가지 않게 한다.
          key={linking.id}
          remediation={{
            id: linking.id,
            symptom: linking.symptom,
            storeName: linking.storeName,
            occurredAt: linking.occurredAt,
          }}
          onClose={() => setLinking(null)}
          onLinked={() => setLinking(null)}
        />
      )}
    </div>
  );
}

// =============================================================
// 펌웨어 탭 — 3-track display + history (PDF p.12)
// =============================================================

export function FirmwareTab({ robotId }: { robotId: string }) {
  const userRole = useAuthStore((s) => s.user?.role);
  // §D-4 — manufacturing & admin record firmware adoption events.
  // VIEWER / SALES / FIELD don't have a write surface here.
  const canCreate =
    userRole === UserRole.ADMIN ||
    userRole === UserRole.MANAGER ||
    userRole === UserRole.MFG_MANAGER;
  const [createOpen, setCreateOpen] = useState(false);

  const latestQ = useQuery({
    queryKey: ['robot-firmware-latest', robotId],
    queryFn: () => firmwareEventsApi.latestForRobot(robotId),
    enabled: !!robotId,
  });
  const historyQ = useQuery({
    queryKey: ['robot-firmware-history', robotId],
    queryFn: () => firmwareEventsApi.listForRobot(robotId),
    enabled: !!robotId,
  });

  return (
    <div className="rd-stack">
      <FirmwareEventCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        robotId={robotId}
      />
      {/* 지금 버전 — 세 갈래(바이너리 · 번들 · 태블릿 앱)를 한 판에 나란히. */}
      <Sheet>
        <SheetHead
          title="지금 버전"
          actions={
            canCreate && (
              <Button type="button" onClick={() => setCreateOpen(true)}>
                + 새 펌웨어 이벤트
              </Button>
            )
          }
        />
        {latestQ.isLoading ? (
          <SheetEmpty>불러오는 중…</SheetEmpty>
        ) : (
          <div className="rd-cols">
            {(latestQ.data ?? []).map((entry: LatestFirmwareEventEntry) => (
              <FactSection key={entry.track} title={TRACK_LABEL[entry.track]}>
                {entry.latest ? (
                  <>
                    <div className="rd-version">{entry.latest.version}</div>
                    <div style={subLine}>적용일 {kstYmd(entry.latest.appliedAt) ?? '-'}</div>
                  </>
                ) : (
                  <p className="rd-empty">기록 없음</p>
                )}
              </FactSection>
            ))}
          </div>
        )}
      </Sheet>

      <Sheet>
        <SheetHead title="전체 이력" count={historyQ.data?.length} />
        {historyQ.isLoading && <SheetEmpty>불러오는 중…</SheetEmpty>}
        {historyQ.data && historyQ.data.length === 0 && <SheetEmpty>기록 없음</SheetEmpty>}
        {historyQ.data && historyQ.data.length > 0 && (
          <SheetTable>
            <table style={tableBase}>
              <thead>
                <tr>
                  <th style={th}>적용일</th>
                  <th style={th}>트랙</th>
                  <th style={th}>버전</th>
                  <th style={th}>메모</th>
                  <th style={th}>적용자</th>
                </tr>
              </thead>
              <tbody>
                {historyQ.data.map((e: FirmwareEventDto) => (
                  <tr key={e.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <td style={tdMono}>{kstYmd(e.appliedAt) ?? <Dash />}</td>
                    <td style={td}>{TRACK_LABEL[e.track]}</td>
                    <td style={{ ...tdMono, fontWeight: 600 }}>{e.version}</td>
                    <td style={td}>{e.note ?? <Dash />}</td>
                    <td style={td}>{e.appliedBy?.displayName ?? <Dash />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SheetTable>
        )}
      </Sheet>
    </div>
  );
}

// =============================================================
// 역추적 탭 — cross-reference by model / firmware / location (PDF p.21)
// =============================================================

export function TraceTab({ robot }: { robot: RobotSummaryDto }) {
  const sameModelQ = useQuery({
    queryKey: ['trace-same-model', robot.model.code],
    queryFn: () => robotsApi.list({ modelCode: robot.model.code, pageSize: 100 }),
  });
  const sameLocationQ = useQuery({
    queryKey: ['trace-same-location', robot.locationCategory],
    queryFn: () => robotsApi.list({ locationCategory: robot.locationCategory, pageSize: 100 }),
  });

  const sameModel = (sameModelQ.data?.items ?? []).filter((r) => r.id !== robot.id);
  const sameLocation = (sameLocationQ.data?.items ?? []).filter((r) => r.id !== robot.id);
  // 2026-11-02 — 상태 · 위치가 영문 코드(DEPLOYED · CUSTOMER_SITE)로 보이던 것을 정본 라벨로.
  const locationLabel = ROBOT_LOCATION_LABELS[robot.locationCategory] ?? robot.locationCategory;

  return (
    <div className="rd-stack">
      {/* 2026-10-01 — 화면에 개발 메모(PDF 쪽수 · 모델 이름)가 그대로 보이던 것을 사람의 말로. */}
      <p className="rd-note">
        부품, 펌웨어, 솔루션을 함께 봅니다. 지금은 같은 모델과 같은 위치로만 묶어 보여 주고, 부품
        묶음과 솔루션 코드로 묶는 기능은 나중에 추가됩니다.
      </p>
      <TraceList
        title={`같은 모델 로봇 · ${robot.model.code}`}
        total={sameModel.length}
        rows={sameModel.slice(0, 20).map((r) => ({
          id: r.id,
          label: r.serialShort ?? r.serialLong ?? r.id,
          extra: ROBOT_STATUS_LABELS[r.status] ?? r.status,
        }))}
        moreCount={Math.max(0, sameModel.length - 20)}
      />
      <TraceList
        title={`같은 위치 로봇 · ${locationLabel}`}
        total={sameLocation.length}
        rows={sameLocation.slice(0, 20).map((r) => ({
          id: r.id,
          label: r.serialShort ?? r.serialLong ?? r.id,
          extra: r.model.code,
        }))}
        moreCount={Math.max(0, sameLocation.length - 20)}
      />
    </div>
  );
}

function TraceList({
  title,
  total,
  rows,
  moreCount,
}: {
  title: string;
  total: number;
  rows: { id: string; label: string; extra?: string }[];
  moreCount: number;
}) {
  return (
    <Sheet>
      <SheetHead title={title} count={total} />
      {rows.length === 0 ? (
        <SheetEmpty>해당 없음</SheetEmpty>
      ) : (
        <div className="rd-chips">
          {rows.map((r) => (
            <Link key={r.id} to={`/robots/${r.id}`} className="rd-chip-link">
              <span className="rd-mono">{r.label}</span>
              {r.extra && <span className="rd-chip-extra">{r.extra}</span>}
            </Link>
          ))}
          {moreCount > 0 && <span style={subLine}>그 밖에 {moreCount}대</span>}
        </div>
      )}
    </Sheet>
  );
}

// =============================================================
// Shared bits
// =============================================================

function Empty({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '24px 12px',
        textAlign: 'center',
        color: 'var(--fg-muted)',
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: 10,
        background: 'var(--bg-danger-subtle)',
        border: '1px solid var(--fg-danger)',
        color: 'var(--fg-danger)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12,
      }}
    >
      {text}
    </div>
  );
}

function Hint({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: 10,
        background: 'var(--bg-sunken)',
        border: '1px solid var(--border-default)',
        color: 'var(--fg-muted)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 11,
        lineHeight: 1.5,
      }}
    >
      {text}
    </div>
  );
}

/** 표 칸의 빈 값. */
function Dash() {
  return <span style={{ color: 'var(--fg-subtle)' }}>–</span>;
}

/** 표 칸 안 둘째 줄(이벤트명 · 주소 · 회수 메모). */
const subLine: CSSProperties = { marginTop: 2, fontSize: 12.5, color: 'var(--fg-muted)' };

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14,
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)', fontSize: 13.5 };
const tdStrong: CSSProperties = { ...td, fontWeight: 550 };
